import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  ArrowLeft, Users, Heart, MessageCircle, DollarSign, TrendingUp, Flag,
  UserCheck, Ban, BadgeCheck, Trash2, Search, ShieldAlert, Video, ScrollText, Plus, X,
  ConciergeBell, Send, UserPlus, UserCog, Eye, Lock, Zap, Megaphone, CheckCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { MemberManageDialog } from '@/components/MatchmakerDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PLAN_LABEL = { free: 'Free', standard: 'Standard', premium: 'Premium', private_match: 'Private Match' };
const ROLE_LABEL = {
  member: 'Member', matchmaker: 'Matchmaker', finance: 'Finance', support: 'Support (L1)',
  support_lead: 'Support Lead (L2)', live_host: 'Live Host', moderator: 'Moderator', super_admin: 'Super Admin',
};
const ADDON_LABEL = {
  live_match: 'Live Match', profile_boost: 'Profile Boost', super_likes: 'Super Likes',
  background_check: 'Background Check', video_date: 'Video Date',
};
const ROLE_BADGE = {
  super_admin: 'bg-rose-600', moderator: 'bg-purple-600', live_host: 'bg-indigo-600',
  matchmaker: 'bg-pink-500', finance: 'bg-emerald-600', support: 'bg-blue-500',
  support_lead: 'bg-cyan-600', member: 'bg-gray-400',
};
const STAFF_ROLE_SET = ['super_admin', 'moderator', 'matchmaker', 'finance', 'support', 'support_lead', 'live_host'];

const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` } });

const AdminPage = ({ user }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const perms = user.permissions || [];
  const can = (p) => perms.includes(p);

  const fetchStats = useCallback(async () => {
    if (!perms.includes('stats.read')) return;
    try { setStats((await axios.get(`${API}/admin/stats`, authHeaders())).data); }
    catch (e) { /* non-blocking */ }
  }, [perms]);

  const [msgUnread, setMsgUnread] = useState(0);
  useEffect(() => {
    if (!user.is_staff) return undefined;
    const fetchUnread = async () => {
      try { setMsgUnread((await axios.get(`${API}/admin/messages-unread-count`, authHeaders())).data.threads || 0); }
      catch (e) {}
    };
    fetchUnread();
    const t = setInterval(fetchUnread, 20000);
    return () => clearInterval(t);
  }, [user.is_staff]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const tabs = [
    can('stats.read') && { v: 'overview', label: 'Overview' },
    can('users.read') && { v: 'users', label: 'Users' },
    can('reports.read') && { v: 'reports', label: `Reports${stats?.pending_reports ? ` (${stats.pending_reports})` : ''}` },
    can('payments.read') && { v: 'payments', label: 'Payments' },
    can('services.manage') && { v: 'concierge', label: 'Concierge' },
    can('services.manage') && { v: 'matchmaking', label: 'Matchmaking' },
    user.is_staff && { v: 'messages', label: (
      <span className="flex items-center gap-1.5">Messages{msgUnread > 0 && <span data-testid="messages-tab-badge" className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold rounded-full bg-rose-500 text-white">{msgUnread}</span>}</span>
    ) },
    can('live.read') && { v: 'live', label: 'Live Hosting' },
    can('roles.manage') && { v: 'roles', label: 'Roles' },
    can('audit.read') && { v: 'audit', label: 'Audit Log' },
  ].filter(Boolean);

  const defaultTab = tabs[0]?.v;
  const [activeTab, setActiveTab] = useState(defaultTab);
  const location = useLocation();
  useEffect(() => {
    const t = new URLSearchParams(location.search).get('tab');
    if (t) setActiveTab(t);
  }, [location.search]);
  const [usersFilter, setUsersFilter] = useState({});
  const navigateTab = (tab, filter) => { if (filter !== undefined) setUsersFilter(filter || {}); setActiveTab(tab); };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-amber-50">
      <div className="max-w-7xl mx-auto px-6 py-10">
        <Button data-testid="back-to-dashboard-btn" variant="ghost" onClick={() => navigate('/dashboard')} className="mb-6 text-rose-700">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
        </Button>

        <div className="flex items-center gap-3 mb-2">
          <ShieldAlert className="w-9 h-9 text-rose-600" />
          <h1 className="text-5xl font-bold text-rose-900" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {user.role === 'live_host' ? 'Hosting Console' : 'Admin Dashboard'}
          </h1>
        </div>
        <div className="text-gray-500 mb-8 flex items-center gap-2" data-testid="admin-role-label">Signed in as <Badge className={ROLE_BADGE[user.role]}>{ROLE_LABEL[user.role] || user.role}</Badge></div>

        {tabs.length === 0 ? (
          <p className="text-gray-500">You do not have access to any admin sections.</p>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="mb-8 flex-wrap h-auto">
              {tabs.map((t) => <TabsTrigger key={t.v} value={t.v} data-testid={`tab-${t.v}`}>{t.label}</TabsTrigger>)}
            </TabsList>

            {can('stats.read') && <TabsContent value="overview"><Overview stats={stats} onNavigate={navigateTab} isSuperAdmin={user.role === 'super_admin'} /></TabsContent>}
            {can('users.read') && <TabsContent value="users"><UsersTab onChange={fetchStats} currentUserId={user.id} can={can} filter={usersFilter} onClearFilter={() => setUsersFilter({})} /></TabsContent>}
            {can('reports.read') && <TabsContent value="reports"><ReportsTab onChange={fetchStats} can={can} /></TabsContent>}
            {can('payments.read') && <TabsContent value="payments"><PaymentsTab /></TabsContent>}
            {can('services.manage') && <TabsContent value="concierge"><ConciergeTab role={user.role} /></TabsContent>}
            {can('services.manage') && <TabsContent value="matchmaking"><MatchmakingTab /></TabsContent>}
            {user.is_staff && <TabsContent value="messages"><MessagesTab currentUser={user} /></TabsContent>}
            {can('live.read') && <TabsContent value="live"><LiveHostingTab can={can} /></TabsContent>}
            {can('roles.manage') && <TabsContent value="roles"><RolesTab /></TabsContent>}
            {can('audit.read') && <TabsContent value="audit"><AuditTab /></TabsContent>}
          </Tabs>
        )}
      </div>
    </div>
  );
};

const ACCENT = {
  rose: 'bg-rose-100', green: 'bg-green-100', blue: 'bg-blue-100',
  emerald: 'bg-emerald-100', amber: 'bg-amber-100', purple: 'bg-purple-100', red: 'bg-red-100', indigo: 'bg-indigo-100',
};

const StatCard = ({ icon, title, value, accent = 'rose', onClick }) => (
  <Card
    className={`shadow-lg border-rose-100 ${onClick ? 'cursor-pointer hover:shadow-xl hover:-translate-y-0.5 transition-all' : ''}`}
    data-testid={`stat-${title.toLowerCase().replace(/\s+/g, '-')}`}
    onClick={onClick}
  >
    <CardContent className="p-5 flex items-center gap-4">
      <div className={`p-3 rounded-lg ${ACCENT[accent] || ACCENT.rose}`}>{icon}</div>
      <div>
        <p className="text-sm text-gray-600">{title}</p>
        <p className="text-2xl font-bold text-rose-900">{value}</p>
      </div>
    </CardContent>
  </Card>
);

const Overview = ({ stats, onNavigate, isSuperAdmin }) => {
  const [showMatches, setShowMatches] = useState(false);
  const [showMessages, setShowMessages] = useState(false);
  if (!stats) return <p className="text-gray-500">Loading...</p>;
  const totalSubs = stats.plan_breakdown.standard + stats.plan_breakdown.premium + stats.plan_breakdown.private_match;
  return (
    <div className="space-y-8">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <StatCard icon={<Users className="w-7 h-7 text-rose-500" />} title="Total Users" value={stats.total_users} onClick={() => onNavigate('users', {})} />
        <StatCard icon={<UserCheck className="w-7 h-7 text-green-500" />} title="Active Users" value={stats.active_users} accent="green" onClick={() => onNavigate('users', { status: 'active' })} />
        <StatCard icon={<Heart className="w-7 h-7 text-rose-500" />} title="Matches" value={stats.total_matches} onClick={() => setShowMatches(true)} />
        <StatCard icon={<MessageCircle className="w-7 h-7 text-blue-500" />} title="Messages" value={stats.total_messages} accent="blue" onClick={() => setShowMessages(true)} />
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <StatCard icon={<DollarSign className="w-7 h-7 text-emerald-600" />} title="MRR" value={`$${stats.mrr}`} accent="emerald" onClick={() => onNavigate('payments')} />
        <StatCard icon={<TrendingUp className="w-7 h-7 text-amber-600" />} title="Add-on Revenue" value={`$${stats.addon_revenue}`} accent="amber" onClick={() => onNavigate('payments')} />
        <StatCard icon={<Users className="w-7 h-7 text-purple-500" />} title="New Today" value={stats.new_users_today} accent="purple" onClick={() => onNavigate('users', { created: 'today' })} />
        <StatCard icon={<Flag className="w-7 h-7 text-red-500" />} title="Pending Reports" value={stats.pending_reports} accent="red" onClick={() => onNavigate('reports')} />
      </div>
      <Card className="shadow-xl border-rose-100">
        <CardHeader><CardTitle>Subscription Breakdown ({totalSubs} paying members)</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {Object.entries(stats.plan_breakdown).map(([plan, count]) => {
            const pct = stats.total_users ? Math.round((count / stats.total_users) * 100) : 0;
            return (
              <div key={plan} data-testid={`plan-row-${plan}`}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="font-medium text-gray-700">{PLAN_LABEL[plan]}</span>
                  <span className="text-gray-500">{count} ({pct}%)</span>
                </div>
                <div className="h-2 bg-rose-50 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-amber-400 to-rose-500" style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>
      <MatchesDialog open={showMatches} onOpenChange={setShowMatches} />
      <MessagesDialog open={showMessages} onOpenChange={setShowMessages} isSuperAdmin={isSuperAdmin} />
    </div>
  );
};

const MATCH_STATUS_BADGE = {
  accepted: 'bg-green-100 text-green-700', pending: 'bg-amber-100 text-amber-700',
  rejected: 'bg-gray-200 text-gray-600', proposed: 'bg-blue-100 text-blue-700',
};

const MatchesDialog = ({ open, onOpenChange }) => {
  const [items, setItems] = useState(null);
  useEffect(() => {
    if (!open) return;
    setItems(null);
    axios.get(`${API}/admin/matches`, authHeaders()).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="matches-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Heart className="w-5 h-5 text-rose-500" /> Matches</DialogTitle>
        </DialogHeader>
        {items === null ? <p className="text-gray-500 py-8 text-center">Loading...</p>
          : items.length === 0 ? <p className="text-gray-400 py-8 text-center">No matches yet.</p>
          : (
            <Table>
              <TableHeader><TableRow><TableHead>Members</TableHead><TableHead>Status</TableHead><TableHead>Score</TableHead><TableHead>Created</TableHead></TableRow></TableHeader>
              <TableBody>
                {items.map((m) => (
                  <TableRow key={m.id} data-testid={`match-row-${m.id}`}>
                    <TableCell className="font-medium">{m.user1_name} &amp; {m.user2_name}</TableCell>
                    <TableCell><Badge className={MATCH_STATUS_BADGE[m.status] || 'bg-gray-100 text-gray-600'}>{m.status}</Badge></TableCell>
                    <TableCell className="text-sm">{m.compatibility_score != null ? `${Math.round(m.compatibility_score)}%` : '-'}</TableCell>
                    <TableCell className="text-sm text-gray-500">{m.created_at ? new Date(m.created_at).toLocaleDateString() : '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
      </DialogContent>
    </Dialog>
  );
};

const MessagesDialog = ({ open, onOpenChange, isSuperAdmin }) => {
  const [items, setItems] = useState(null);
  const [thread, setThread] = useState(null); // {participants, messages}
  useEffect(() => {
    if (!open) return;
    setItems(null); setThread(null);
    axios.get(`${API}/admin/messages-recent`, authHeaders()).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [open]);
  const viewThread = async (conv) => {
    const reason = window.prompt('Viewing private message content is logged for compliance.\nOptional: enter a reason for access.', '');
    if (reason === null) return; // cancelled
    try {
      const res = await axios.get(`${API}/admin/conversations/${conv.match_id}/messages${reason ? `?reason=${encodeURIComponent(reason)}` : ''}`, authHeaders());
      setThread({ participants: conv.participants, messages: res.data });
    } catch (e) { toast.error('Could not load conversation'); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="messages-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><MessageCircle className="w-5 h-5 text-blue-500" /> {thread ? `Conversation · ${thread.participants?.join(' & ')}` : 'Recent Conversations'}</DialogTitle>
        </DialogHeader>
        {thread ? (
          <div className="space-y-3">
            <Button size="sm" variant="ghost" className="text-rose-700" onClick={() => setThread(null)} data-testid="messages-back-btn"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
            <div className="space-y-2" data-testid="conversation-thread">
              {thread.messages.length === 0 ? <p className="text-gray-400 text-center py-6">No messages.</p>
                : thread.messages.map((m) => (
                  <div key={m.id} className="border border-gray-100 rounded-lg p-2">
                    <p className="text-xs font-semibold text-rose-700">{m.sender_name}<span className="text-gray-400 font-normal"> · {m.created_at ? new Date(m.created_at).toLocaleString() : ''}</span></p>
                    <p className="text-sm text-gray-700">{m.content}</p>
                  </div>
                ))}
            </div>
          </div>
        ) : !isSuperAdmin ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-xs text-gray-500 bg-gray-50 rounded-lg p-3" data-testid="messages-privacy-note">
              <Lock className="w-4 h-4" /> Message content is restricted. Only a Super Admin can view conversation contents for compliance.
            </div>
            <ConversationMetaList items={items} />
          </div>
        ) : (
          <ConversationMetaList items={items} onView={viewThread} />
        )}
      </DialogContent>
    </Dialog>
  );
};

const ConversationMetaList = ({ items, onView }) => {
  if (items === null) return <p className="text-gray-500 py-8 text-center">Loading...</p>;
  if (items.length === 0) return <p className="text-gray-400 py-8 text-center">No conversations yet.</p>;
  return (
    <div className="space-y-2">
      {items.map((c) => (
        <div key={c.match_id} className="flex items-center justify-between border border-gray-100 rounded-lg p-3" data-testid={`conversation-${c.match_id}`}>
          <div>
            <p className="font-medium text-sm text-rose-900">{c.participants?.join(' & ') || 'Unknown'}</p>
            <p className="text-xs text-gray-400">{c.message_count} message{c.message_count === 1 ? '' : 's'} · last {c.last_at ? new Date(c.last_at).toLocaleString() : '-'}</p>
          </div>
          {onView && (
            <Button size="sm" variant="outline" className="text-blue-600" data-testid={`view-thread-${c.match_id}`} onClick={() => onView(c)}>
              <Eye className="w-4 h-4 mr-1" /> View
            </Button>
          )}
        </div>
      ))}
    </div>
  );
};

const UsersTab = ({ onChange, currentUserId, can, filter = {}, onClearFilter }) => {
  const [data, setData] = useState({ total: 0, users: [] });
  const [search, setSearch] = useState('');
  const [skip, setSkip] = useState(0);
  const limit = 25;

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams({ search, skip: String(skip), limit: String(limit) });
      if (filter.created) params.set('created', filter.created);
      if (filter.status) params.set('status', filter.status);
      setData((await axios.get(`${API}/admin/users?${params.toString()}`, authHeaders())).data);
    } catch (e) { toast.error('Failed to load users'); }
  }, [search, skip, filter.created, filter.status]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSkip(0); }, [filter.created, filter.status]);

  const filterLabel = filter.created === 'today' ? 'New today'
    : filter.created === 'week' ? 'New this week'
    : filter.status === 'active' ? 'Active only'
    : filter.status === 'blocked' ? 'Blocked only' : null;

  const action = async (fn, msg) => {
    try { await fn(); toast.success(msg); load(); onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
  };

  return (
    <Card className="shadow-xl border-rose-100">
      <CardHeader>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 max-w-sm">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input data-testid="user-search-input" placeholder="Search name or email..." value={search}
              onChange={(e) => { setSkip(0); setSearch(e.target.value); }} className="pl-9" />
          </div>
          {filterLabel && (
            <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-200 cursor-pointer" data-testid="users-filter-chip" onClick={onClearFilter}>
              {filterLabel} <X className="w-3 h-3 ml-1" />
            </Badge>
          )}
          <span className="text-sm text-gray-500">{data.total} users</span>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Plan</TableHead>
                <TableHead>Role</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.users.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                  <TableCell className="font-medium">
                    {u.name} {u.is_verified && <BadgeCheck className="inline w-4 h-4 text-blue-500 ml-1" />}
                  </TableCell>
                  <TableCell className="text-gray-600">{u.email}</TableCell>
                  <TableCell><Badge variant="outline">{PLAN_LABEL[u.plan_type]}</Badge></TableCell>
                  <TableCell>
                    {can('roles.manage') && u.id !== currentUserId ? (
                      <Select value={u.role || 'member'} onValueChange={(v) => {
                        if (v === u.role) return;
                        const reason = window.prompt(`Change ${u.name}'s role to "${ROLE_LABEL[v]}"?\nOptional: enter a reason (logged for compliance).`, '');
                        if (reason === null) return; // cancelled
                        action(() => axios.post(`${API}/admin/users/${u.id}/role?role=${v}${reason ? `&reason=${encodeURIComponent(reason)}` : ''}`, {}, authHeaders()), `Role set to ${ROLE_LABEL[v]}`);
                      }}>
                        <SelectTrigger className="h-8 w-40" data-testid={`role-select-${u.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Object.keys(ROLE_LABEL).map((r) => <SelectItem key={r} value={r}>{ROLE_LABEL[r]}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge className={ROLE_BADGE[u.role] || ROLE_BADGE.member}>{ROLE_LABEL[u.role] || u.role}</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {u.is_active
                      ? <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Active</Badge>
                      : <Badge className="bg-red-100 text-red-700 hover:bg-red-100">Blocked</Badge>}
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    {!STAFF_ROLE_SET.includes(u.role || 'member') && (
                      <QuickMessageButton userId={u.id} userName={u.name} />
                    )}
                    {can('services.manage') && !STAFF_ROLE_SET.includes(u.role || 'member') && (
                      <MemberManageDialog userId={u.id} userName={u.name} onChanged={load}>
                        <Button data-testid={`manage-btn-${u.id}`} size="sm" variant="ghost" className="text-indigo-600">
                          <UserCog className="w-4 h-4" />
                        </Button>
                      </MemberManageDialog>
                    )}
                    {can('users.moderate') && (u.is_active ? (
                      <Button data-testid={`block-btn-${u.id}`} size="sm" variant="ghost" className="text-red-600"
                        onClick={() => action(() => axios.post(`${API}/admin/users/${u.id}/block`, {}, authHeaders()), 'User blocked')}>
                        <Ban className="w-4 h-4" />
                      </Button>
                    ) : (
                      <Button data-testid={`unblock-btn-${u.id}`} size="sm" variant="ghost" className="text-green-600"
                        onClick={() => action(() => axios.post(`${API}/admin/users/${u.id}/unblock`, {}, authHeaders()), 'User unblocked')}>
                        <UserCheck className="w-4 h-4" />
                      </Button>
                    ))}
                    {can('users.moderate') && (
                      <Button data-testid={`verify-btn-${u.id}`} size="sm" variant="ghost" className="text-blue-600"
                        onClick={() => action(() => axios.post(`${API}/admin/users/${u.id}/verify?verified=${!u.is_verified}`, {}, authHeaders()), 'Verification updated')}>
                        <BadgeCheck className="w-4 h-4" />
                      </Button>
                    )}
                    {can('users.delete') && u.id !== currentUserId && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button data-testid={`delete-btn-${u.id}`} size="sm" variant="ghost" className="text-gray-500"><Trash2 className="w-4 h-4" /></Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete {u.name}?</AlertDialogTitle>
                            <AlertDialogDescription>This permanently removes the user and their matches, sessions, and subscription. This cannot be undone.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction data-testid={`confirm-delete-${u.id}`} className="bg-red-600 hover:bg-red-700"
                              onClick={() => action(() => axios.delete(`${API}/admin/users/${u.id}`, authHeaders()), 'User deleted')}>Delete</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {data.total > limit && (
          <div className="flex justify-between items-center mt-4">
            <Button variant="outline" size="sm" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - limit))}>Previous</Button>
            <span className="text-sm text-gray-500">Showing {skip + 1}–{Math.min(skip + limit, data.total)} of {data.total}</span>
            <Button variant="outline" size="sm" disabled={skip + limit >= data.total} onClick={() => setSkip(skip + limit)}>Next</Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const ReportsTab = ({ onChange, can }) => {
  const [reports, setReports] = useState([]);
  const load = useCallback(async () => {
    try { setReports((await axios.get(`${API}/admin/reports`, authHeaders())).data); }
    catch (e) { toast.error('Failed to load reports'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try { await axios.post(`${API}/admin/reports/${id}/resolve?status=${status}`, {}, authHeaders()); toast.success(`Report ${status}`); load(); onChange(); }
    catch (e) { toast.error('Action failed'); }
  };

  if (reports.length === 0) return <Card className="shadow-xl border-rose-100"><CardContent className="py-12 text-center text-gray-500">No reports to review</CardContent></Card>;
  return (
    <div className="space-y-4">
      {reports.map((r) => (
        <Card key={r.id} className="shadow-lg border-rose-100" data-testid={`report-${r.id}`}>
          <CardContent className="p-5">
            <div className="flex justify-between items-start mb-2">
              <div>
                <p className="font-semibold text-rose-900">Reason: {r.reason}</p>
                <p className="text-sm text-gray-500">{r.reporter?.name || 'Unknown'} reported {r.reported_user?.name || 'Unknown'}</p>
              </div>
              <Badge className={r.status === 'pending' ? 'bg-yellow-100 text-yellow-700' : r.status === 'resolved' ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-700'}>{r.status}</Badge>
            </div>
            {r.details && <p className="text-sm text-gray-600 mb-3">{r.details}</p>}
            <p className="text-xs text-gray-400 mb-3">Submitted {new Date(r.created_at).toLocaleString()}</p>
            {r.status === 'pending' && can('reports.moderate') && (
              <div className="flex gap-2">
                <Button data-testid={`resolve-report-${r.id}`} size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => setStatus(r.id, 'resolved')}>Resolve</Button>
                <Button data-testid={`dismiss-report-${r.id}`} size="sm" variant="outline" onClick={() => setStatus(r.id, 'dismissed')}>Dismiss</Button>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

const PaymentsTab = () => {
  const [data, setData] = useState({ subscriptions: [], addon_purchases: [] });
  useEffect(() => {
    axios.get(`${API}/admin/payments`, authHeaders()).then((res) => setData(res.data)).catch(() => toast.error('Failed to load payments'));
  }, []);
  return (
    <div className="space-y-8">
      <Card className="shadow-xl border-rose-100">
        <CardHeader><CardTitle>Active Subscriptions ({data.subscriptions.length})</CardTitle></CardHeader>
        <CardContent>
          {data.subscriptions.length === 0 ? <p className="text-gray-500">No paid subscriptions yet.</p> : (
            <Table>
              <TableHeader><TableRow><TableHead>Member</TableHead><TableHead>Plan</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Monthly</TableHead></TableRow></TableHeader>
              <TableBody>
                {data.subscriptions.map((s, i) => (
                  <TableRow key={i} data-testid={`sub-row-${i}`}>
                    <TableCell>{s.user?.name}<span className="block text-xs text-gray-400">{s.user?.email}</span></TableCell>
                    <TableCell><Badge variant="outline">{PLAN_LABEL[s.plan_type]}</Badge></TableCell>
                    <TableCell><Badge className="bg-green-100 text-green-700">{s.status}</Badge></TableCell>
                    <TableCell className="text-right font-medium">${s.amount}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      <Card className="shadow-xl border-rose-100">
        <CardHeader><CardTitle>Add-on Purchases ({data.addon_purchases.length})</CardTitle></CardHeader>
        <CardContent>
          {data.addon_purchases.length === 0 ? <p className="text-gray-500">No add-on purchases yet.</p> : (
            <Table>
              <TableHeader><TableRow><TableHead>Member</TableHead><TableHead>Add-on</TableHead><TableHead>Date</TableHead><TableHead className="text-right">Amount</TableHead></TableRow></TableHeader>
              <TableBody>
                {data.addon_purchases.map((a, i) => (
                  <TableRow key={i} data-testid={`addon-row-${i}`}>
                    <TableCell>{a.user?.name}<span className="block text-xs text-gray-400">{a.user?.email}</span></TableCell>
                    <TableCell>{ADDON_LABEL[a.addon_type] || a.addon_type}</TableCell>
                    <TableCell className="text-sm text-gray-500">{a.created_at ? new Date(a.created_at).toLocaleDateString() : '-'}</TableCell>
                    <TableCell className="text-right font-medium">${a.amount}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const LIVE_STATUS_BADGE = { proposed: 'bg-amber-100 text-amber-700', connected: 'bg-blue-100 text-blue-700', ended: 'bg-gray-200 text-gray-700' };

const LiveHostingTab = ({ can }) => {
  const [hstats, setHstats] = useState(null);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]); // [{id,name}]
  const [notes, setNotes] = useState('');
  const [liveMatches, setLiveMatches] = useState([]);
  const [queue, setQueue] = useState([]);
  const manage = can('live.match.manage');

  const loadStats = useCallback(async () => {
    try { setHstats((await axios.get(`${API}/admin/host/stats`, authHeaders())).data); } catch (e) {}
  }, []);
  const loadUsers = useCallback(async () => {
    try { setUsers((await axios.get(`${API}/admin/host/users?search=${encodeURIComponent(search)}`, authHeaders())).data); }
    catch (e) { toast.error('Failed to load users'); }
  }, [search]);
  const loadMatches = useCallback(async () => {
    try { setLiveMatches((await axios.get(`${API}/admin/host/live-matches`, authHeaders())).data); }
    catch (e) { toast.error('Failed to load live matches'); }
  }, []);
  const loadQueue = useCallback(async () => {
    try { setQueue((await axios.get(`${API}/admin/host/live-queue`, authHeaders())).data); } catch (e) {}
  }, []);

  useEffect(() => { loadStats(); loadMatches(); }, [loadStats, loadMatches]);
  useEffect(() => { loadUsers(); }, [loadUsers]);
  useEffect(() => {
    loadQueue();
    const t = setInterval(loadQueue, 8000);
    return () => clearInterval(t);
  }, [loadQueue]);

  const toggleSelect = (u) => {
    setSelected((prev) => {
      if (prev.find((p) => p.id === u.id)) return prev.filter((p) => p.id !== u.id);
      if (prev.length >= 2) { toast.info('You can select two people for a live match'); return prev; }
      return [...prev, { id: u.id, name: u.name }];
    });
  };

  const createMatch = async () => {
    try {
      await axios.post(`${API}/admin/host/live-matches`, { user1_id: selected[0].id, user2_id: selected[1].id, notes }, authHeaders());
      toast.success('Live match created');
      setSelected([]); setNotes('');
      loadMatches(); loadStats();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create'); }
  };

  const updateMatch = async (id, payload, msg) => {
    try { await axios.patch(`${API}/admin/host/live-matches/${id}`, payload, authHeaders()); toast.success(msg); loadMatches(); loadStats(); }
    catch (e) { toast.error('Update failed'); }
  };

  const claimQueue = async (item) => {
    try {
      await axios.post(`${API}/admin/host/live-queue/${item.id}/claim`, {}, authHeaders());
      if (item.user) toggleSelect(item.user);
      toast.success(`Claimed ${item.user?.name || 'member'} — pick a second person to pair`);
      loadQueue();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to claim'); }
  };
  const serviceQueue = async (id) => {
    try { await axios.post(`${API}/admin/host/live-queue/${id}/done`, {}, authHeaders()); toast.success('Marked serviced'); loadQueue(); }
    catch (e) { toast.error('Failed to update'); }
  };

  return (
    <div className="space-y-8">
      {hstats && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          <StatCard icon={<Video className="w-7 h-7 text-indigo-500" />} title="My Live Matches" value={hstats.my_live_matches} accent="indigo" />
          <StatCard icon={<Heart className="w-7 h-7 text-rose-500" />} title="My Successful" value={hstats.my_successful_matches} />
          <StatCard icon={<Video className="w-7 h-7 text-blue-500" />} title="All Live Matches" value={hstats.total_live_matches} accent="blue" />
          <StatCard icon={<Heart className="w-7 h-7 text-green-500" />} title="Total Matched" value={hstats.successful_matches} accent="green" />
        </div>
      )}

      <Card className="shadow-xl border-amber-200 bg-amber-50/40" data-testid="live-queue-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-amber-500" /> Paid Live Match Queue
            {queue.length > 0 && <Badge className="bg-amber-500 text-white" data-testid="live-queue-count">{queue.length}</Badge>}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {queue.length === 0 ? (
            <p className="text-sm text-gray-500">No one is waiting. New paid Live Match Events appear here automatically (auto-refreshes every few seconds).</p>
          ) : (
            <div className="space-y-3">
              {queue.map((q) => (
                <div key={q.id} className="flex items-center justify-between gap-3 border border-amber-200 bg-white rounded-lg p-3 flex-wrap" data-testid={`live-queue-${q.id}`}>
                  <div className="flex items-center gap-3">
                    {q.user?.picture ? <img src={q.user.picture} alt="" className="w-10 h-10 rounded-full object-cover" /> : <div className="w-10 h-10 rounded-full bg-amber-100" />}
                    <div>
                      <p className="font-semibold text-amber-900" data-testid={`live-queue-name-${q.id}`}>{q.user?.name || 'Member'}</p>
                      <p className="text-xs text-gray-500">Paid {q.created_at ? new Date(q.created_at).toLocaleString() : ''}{q.assigned_name ? ` · Claimed by ${q.assigned_name}` : ''}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={q.status === 'in_progress' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}>{q.status === 'in_progress' ? 'Claimed' : 'Waiting'}</Badge>
                    {manage && <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-white" data-testid={`claim-live-${q.id}`} onClick={() => claimQueue(q)}>Claim &amp; Pair</Button>}
                    {manage && <Button size="sm" variant="outline" data-testid={`serviced-live-${q.id}`} onClick={() => serviceQueue(q.id)}>Mark Serviced</Button>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {manage && (
        <Card className="shadow-xl border-rose-100">
          <CardHeader><CardTitle>Create a Live Match</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="relative max-w-sm">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input data-testid="host-user-search" placeholder="Search members..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
            </div>
            {selected.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap" data-testid="selected-users">
                <span className="text-sm text-gray-600">Selected:</span>
                {selected.map((s) => (
                  <Badge key={s.id} className="bg-indigo-600 cursor-pointer" onClick={() => toggleSelect(s)}>{s.name} <X className="w-3 h-3 ml-1" /></Badge>
                ))}
                {selected.length === 2 && (
                  <>
                    <Input placeholder="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} className="max-w-xs ml-2" data-testid="live-match-notes" />
                    <Button data-testid="create-live-match-btn" size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={createMatch}>
                      <Plus className="w-4 h-4 mr-1" /> Pair Them
                    </Button>
                  </>
                )}
              </div>
            )}
            <div className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Plan</TableHead><TableHead>Matches</TableHead><TableHead>Live Matches</TableHead><TableHead className="text-right">Select</TableHead></TableRow></TableHeader>
                <TableBody>
                  {users.map((u) => {
                    const isSel = selected.find((p) => p.id === u.id);
                    return (
                      <TableRow key={u.id} data-testid={`host-user-row-${u.id}`}>
                        <TableCell className="font-medium">{u.name}<span className="block text-xs text-gray-400">{u.email}</span></TableCell>
                        <TableCell><Badge variant="outline">{PLAN_LABEL[u.plan_type]}</Badge></TableCell>
                        <TableCell>{u.total_matches}</TableCell>
                        <TableCell>{u.live_matches}</TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant={isSel ? 'default' : 'outline'} className={isSel ? 'bg-indigo-600' : ''} data-testid={`select-user-${u.id}`} onClick={() => toggleSelect(u)}>
                            {isSel ? 'Selected' : 'Select'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="shadow-xl border-rose-100">
        <CardHeader><CardTitle>Live Matches ({liveMatches.length})</CardTitle></CardHeader>
        <CardContent>
          {liveMatches.length === 0 ? <p className="text-gray-500">No live matches yet.</p> : (
            <div className="space-y-3">
              {liveMatches.map((m) => (
                <div key={m.id} className="border border-rose-100 rounded-lg p-4" data-testid={`live-match-${m.id}`}>
                  <div className="flex justify-between items-start flex-wrap gap-2">
                    <div>
                      <p className="font-semibold text-rose-900">{m.user1?.name || '—'} ↔ {m.user2?.name || '—'}</p>
                      <p className="text-xs text-gray-400">Host: {m.host?.name || '—'} · {m.created_at ? new Date(m.created_at).toLocaleString() : ''}</p>
                      {m.notes && <p className="text-sm text-gray-600 mt-1">{m.notes}</p>}
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={LIVE_STATUS_BADGE[m.status] || ''}>{m.status}</Badge>
                      {m.outcome && <Badge variant="outline">{m.outcome}</Badge>}
                    </div>
                  </div>
                  {manage && (
                    <div className="flex gap-2 mt-3 flex-wrap">
                      {m.status === 'proposed' && <Button size="sm" variant="outline" data-testid={`connect-${m.id}`} onClick={() => updateMatch(m.id, { status: 'connected' }, 'Marked connected')}>Mark Connected</Button>}
                      {m.status !== 'ended' && <Button size="sm" variant="outline" data-testid={`end-${m.id}`} onClick={() => updateMatch(m.id, { status: 'ended' }, 'Marked ended')}>End</Button>}
                      <Button size="sm" className="bg-green-600 hover:bg-green-700" data-testid={`matched-${m.id}`} onClick={() => updateMatch(m.id, { outcome: 'matched' }, 'Marked as matched!')}>It's a Match</Button>
                      <Button size="sm" variant="ghost" className="text-gray-500" data-testid={`nomatch-${m.id}`} onClick={() => updateMatch(m.id, { outcome: 'not_matched' }, 'Marked not matched')}>No Match</Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const STAGE_BADGE = {
  mutual: 'bg-green-100 text-green-700', awaiting: 'bg-amber-100 text-amber-700',
  one_accepted: 'bg-blue-100 text-blue-700', declined: 'bg-gray-200 text-gray-500',
  expired: 'bg-gray-200 text-gray-400',
};
const STAGE_LABEL = { mutual: 'Mutual match', awaiting: 'Awaiting both', one_accepted: 'One accepted', declined: 'Declined', expired: 'Expired' };

const MatchmakingTab = () => {
  const [stats, setStats] = useState(null);
  const [props, setProps] = useState([]);
  const [filter, setFilter] = useState('all');
  useEffect(() => {
    axios.get(`${API}/admin/matchmaker/stats`, authHeaders()).then((r) => setStats(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    const q = filter !== 'all' ? `?status=${filter}` : '';
    axios.get(`${API}/admin/proposals${q}`, authHeaders()).then((r) => setProps(r.data)).catch(() => {});
  }, [filter]);
  if (!stats) return <p className="text-gray-500">Loading...</p>;
  const maxFunnel = Math.max(...stats.funnel.map((f) => f.count), 1);
  return (
    <div className="space-y-6" data-testid="matchmaking-tab">
      {/* Funnel */}
      <div>
        <h3 className="text-lg font-semibold text-rose-900 mb-3">Matchmaking Funnel</h3>
        <div className="space-y-2">
          {stats.funnel.map((f) => (
            <div key={f.stage} className="flex items-center gap-3" data-testid={`funnel-${f.stage.replace(/\W+/g, '-').toLowerCase()}`}>
              <span className="text-sm text-gray-600 w-32 shrink-0">{f.stage}</span>
              <div className="flex-1 bg-gray-100 rounded-full h-6 overflow-hidden">
                <div className="h-6 bg-gradient-to-r from-rose-400 to-amber-400 rounded-full flex items-center justify-end pr-2" style={{ width: `${Math.max(f.count / maxFunnel * 100, 6)}%` }}>
                  <span className="text-xs font-semibold text-white">{f.count}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
        <p className="text-sm text-gray-500 mt-2">Overall success rate: <span className="font-bold text-rose-700">{stats.success_rate}%</span> · {stats.expired} expired · {stats.declined} declined</p>
      </div>

      {/* Per-matchmaker */}
      <div>
        <h3 className="text-lg font-semibold text-rose-900 mb-2">Per-Matchmaker Performance</h3>
        <Table>
          <TableHeader><TableRow><TableHead>Matchmaker</TableHead><TableHead>Proposed</TableHead><TableHead>Matched</TableHead><TableHead>Match rate</TableHead></TableRow></TableHeader>
          <TableBody>
            {stats.by_matchmaker.map((m) => (
              <TableRow key={m.name} data-testid={`mm-row-${m.name}`}>
                <TableCell className="font-medium">{m.name || 'Unknown'}</TableCell>
                <TableCell>{m.proposed}</TableCell>
                <TableCell>{m.matched}</TableCell>
                <TableCell><Badge className={m.rate >= 40 ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>{m.rate}%</Badge></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Status board */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-semibold text-rose-900">Introductions Board</h3>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="h-9 w-40" data-testid="mm-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="proposed">Awaiting</SelectItem>
              <SelectItem value="mutual">Matched</SelectItem>
              <SelectItem value="declined">Declined</SelectItem>
              <SelectItem value="expired">Expired</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Table>
          <TableHeader><TableRow><TableHead>Introduction</TableHead><TableHead>Matchmaker</TableHead><TableHead>Stage</TableHead><TableHead>Notes</TableHead></TableRow></TableHeader>
          <TableBody>
            {props.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-gray-400 py-6">No introductions.</TableCell></TableRow>}
            {props.map((p) => (
              <TableRow key={p.id} data-testid={`board-row-${p.id}`}>
                <TableCell className="font-medium">{p.member?.name} &amp; {p.candidate?.name}</TableCell>
                <TableCell className="text-sm text-gray-500">{p.proposed_by_name}</TableCell>
                <TableCell><Badge className={STAGE_BADGE[p.stage] || 'bg-gray-100'}>{STAGE_LABEL[p.stage] || p.stage}</Badge></TableCell>
                <TableCell className="text-xs text-gray-500">{p.decline_reason ? `Passed: "${p.decline_reason}"` : (p.reason || '')}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

const RolesTab = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/roles`, authHeaders()).then((r) => setData(r.data)).catch(() => setData({ roles: {} }));
  }, []);
  if (!data) return <p className="text-gray-500">Loading...</p>;
  const order = ['super_admin', 'moderator', 'matchmaker', 'finance', 'support_lead', 'support', 'live_host', 'member'];
  const roles = order.filter((r) => data.roles[r]);
  return (
    <div className="space-y-4" data-testid="roles-tab">
      <div>
        <h3 className="text-lg font-semibold text-rose-900">Roles &amp; Permissions</h3>
        <p className="text-sm text-gray-500">What each access level can do. Assign roles from the Users tab.</p>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {roles.map((r) => {
          const info = data.roles[r];
          return (
            <div key={r} className="border border-rose-100 rounded-xl p-4" data-testid={`role-card-${r}`}>
              <div className="flex items-center gap-2 mb-1">
                <Badge className={ROLE_BADGE[r] || ROLE_BADGE.member}>{info.label}</Badge>
                {info.is_staff && <span className="text-xs text-gray-400">Staff</span>}
              </div>
              <p className="text-sm text-gray-600 mb-3">{info.description}</p>
              <div className="flex flex-wrap gap-1.5">
                {info.permissions.length === 0
                  ? <span className="text-xs text-gray-400">No admin permissions</span>
                  : info.permissions.map((p) => (
                    <span key={p} className="text-[11px] bg-gray-100 text-gray-600 rounded-full px-2 py-0.5">{p}</span>
                  ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};


const AuditTab = () => {
  const [logs, setLogs] = useState([]);
  useEffect(() => {
    axios.get(`${API}/admin/audit-log`, authHeaders()).then((res) => setLogs(res.data)).catch(() => toast.error('Failed to load audit log'));
  }, []);
  return (
    <Card className="shadow-xl border-rose-100">
      <CardHeader><CardTitle className="flex items-center gap-2"><ScrollText className="w-5 h-5" /> Audit Log</CardTitle></CardHeader>
      <CardContent>
        {logs.length === 0 ? <p className="text-gray-500">No actions logged yet.</p> : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Time</TableHead><TableHead>Actor</TableHead><TableHead>Action</TableHead><TableHead>Target</TableHead></TableRow></TableHeader>
              <TableBody>
                {logs.map((l, i) => (
                  <TableRow key={i} data-testid={`audit-row-${i}`}>
                    <TableCell className="text-sm text-gray-500">{l.created_at ? new Date(l.created_at).toLocaleString() : '-'}</TableCell>
                    <TableCell>{l.actor_email}<span className="block text-xs text-gray-400">{ROLE_LABEL[l.actor_role] || l.actor_role}</span></TableCell>
                    <TableCell><Badge variant="outline">{l.action}</Badge></TableCell>
                    <TableCell className="text-xs text-gray-500 max-w-xs truncate">{l.target_id || '-'}{l.details && Object.keys(l.details).length ? ` · ${JSON.stringify(l.details)}` : ''}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const SERVICE_LABEL = {
  private_match: 'Private Match', background_check: 'Background Check',
  live_match: 'Live Match Event', video_date: 'Guided Video Date',
};
const SVC_STATUS = ['new', 'assigned', 'in_progress', 'completed', 'cancelled'];
const SVC_STATUS_LABEL = { new: 'New', assigned: 'Assigned', in_progress: 'In Progress', completed: 'Completed', cancelled: 'Cancelled' };
const SVC_STATUS_BADGE = {
  new: 'bg-amber-100 text-amber-700', assigned: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-indigo-100 text-indigo-700', completed: 'bg-green-100 text-green-700', cancelled: 'bg-gray-200 text-gray-600',
};

const MessageThreadDialog = ({ userId, userName, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState('');
  const endRef = useRef(null);
  const load = useCallback(async () => {
    try { setMessages((await axios.get(`${API}/admin/messages/${userId}`, authHeaders())).data); } catch (e) {}
  }, [userId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);
  const send = async () => {
    if (!body.trim()) return;
    try {
      const res = await axios.post(`${API}/admin/messages/${userId}`, { body: body.trim() }, authHeaders());
      setMessages((p) => [...p, res.data]); setBody('');
    } catch (e) { toast.error('Failed to send'); }
  };
  return (
    <DialogContent className="max-w-lg">
      <DialogHeader><DialogTitle>Message {userName}</DialogTitle></DialogHeader>
      <div className="h-[320px] overflow-y-auto space-y-2 pr-2" data-testid="admin-thread">
        {messages.length === 0 ? <p className="text-center text-gray-400 py-12">No messages yet.</p> : messages.map((m) => (
          <div key={m.id} className={`flex ${m.from_staff ? 'justify-end' : 'justify-start'}`}>
            <div className="max-w-[75%]">
              <div className={`rounded-2xl px-3 py-2 text-sm ${m.from_staff ? 'bg-rose-500 text-white' : 'bg-gray-100 text-gray-800'}`}>
                {m.from_staff && <p className="text-[10px] opacity-80 mb-0.5">{m.sender_name}</p>}
                {m.title && <p className="font-semibold mb-0.5">{m.title}</p>}
                {m.body}
              </div>
              {m.from_staff && <p className="text-[10px] text-gray-400 mt-0.5 text-right">{m.read_by_user ? 'Read ✓✓' : 'Sent ✓'}</p>}
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <div className="flex gap-2">
        <Textarea data-testid="admin-message-input" value={body} onChange={(e) => setBody(e.target.value)} rows={2} className="resize-none"
          placeholder="Reply to member..." onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }} />
        <Button data-testid="admin-send-btn" onClick={send} className="bg-rose-600 hover:bg-rose-700 self-end h-12 px-4"><Send className="w-4 h-4" /></Button>
      </div>
    </DialogContent>
  );
};

const QuickMessageButton = ({ userId, userName, onClose, children }) => {
  const [open, setOpen] = useState(false);
  const handleChange = (v) => { setOpen(v); if (!v && onClose) onClose(); };
  return (
    <Dialog open={open} onOpenChange={handleChange}>
      <DialogTrigger asChild>
        {children || (
          <Button size="sm" variant="ghost" className="text-blue-600" data-testid={`message-btn-${userId}`} title="Message member">
            <MessageCircle className="w-4 h-4" />
          </Button>
        )}
      </DialogTrigger>
      <MessageThreadDialog userId={userId} userName={userName} onClose={() => handleChange(false)} />
    </Dialog>
  );
};

const RepliesInbox = () => {
  const [rows, setRows] = useState([]);
  const load = useCallback(async () => {
    try { setRows((await axios.get(`${API}/admin/replies-inbox`, authHeaders())).data); } catch (e) {}
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);
  return (
    <Card className="shadow-xl border-rose-200 bg-rose-50/40" data-testid="replies-inbox-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageCircle className="w-5 h-5 text-rose-500" /> Member Replies — Needs Response
          {rows.length > 0 && <Badge className="bg-rose-500 text-white" data-testid="replies-inbox-count">{rows.length}</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <div className="flex items-center gap-2 text-sm text-gray-500 py-2" data-testid="replies-inbox-empty">
            <CheckCircle className="w-4 h-4 text-green-500" /> You're all caught up — no unread member replies. (Auto-refreshes every 15s.)
          </div>
        ) : (
          <div className="space-y-2">
            {rows.map((r) => (
              <QuickMessageButton key={r.user_id} userId={r.user_id} userName={r.user?.name} onClose={load}>
                <div className="flex items-center justify-between gap-3 border border-rose-200 bg-white rounded-lg p-3 cursor-pointer hover:bg-rose-50" data-testid={`reply-row-${r.user_id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    {r.user?.picture ? <img src={r.user.picture} alt="" className="w-9 h-9 rounded-full object-cover" />
                      : <div className="w-9 h-9 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center text-sm font-semibold">{(r.user?.name || '?').charAt(0).toUpperCase()}</div>}
                    <div className="min-w-0">
                      <p className="font-semibold text-rose-900 truncate">{r.user?.name}</p>
                      <p className="text-xs text-gray-500 truncate">{r.last_body}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge className="bg-rose-100 text-rose-700">{r.unread} new</Badge>
                    <span className="text-xs text-gray-400">{r.last_at ? new Date(r.last_at).toLocaleString() : ''}</span>
                  </div>
                </div>
              </QuickMessageButton>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const ConversationsList = ({ currentUser }) => {
  const [scope, setScope] = useState('mine');
  const [rows, setRows] = useState([]);
  const [staff, setStaff] = useState([]);
  const load = useCallback(async () => {
    try { setRows((await axios.get(`${API}/admin/conversations?scope=${encodeURIComponent(scope)}`, authHeaders())).data); }
    catch (e) { setRows([]); }
  }, [scope]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    axios.get(`${API}/admin/conversation-staff`, authHeaders()).then((r) => setStaff(r.data)).catch(() => setStaff([]));
  }, []);
  return (
    <Card className="shadow-xl border-blue-100" data-testid="conversations-card">
      <CardHeader>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <CardTitle className="flex items-center gap-2"><MessageCircle className="w-5 h-5 text-blue-500" /> All Conversations</CardTitle>
          <div className="flex items-center gap-2">
            <Button size="sm" variant={scope === 'mine' ? 'default' : 'outline'} data-testid="conv-scope-mine"
              className={scope === 'mine' ? 'bg-blue-500 hover:bg-blue-600' : ''} onClick={() => setScope('mine')}>My conversations</Button>
            <Button size="sm" variant={scope === 'all' ? 'default' : 'outline'} data-testid="conv-scope-all"
              className={scope === 'all' ? 'bg-blue-500 hover:bg-blue-600' : ''} onClick={() => setScope('all')}>All</Button>
            <Select value={['mine', 'all'].includes(scope) ? '' : scope} onValueChange={(v) => v && setScope(v)}>
              <SelectTrigger className="w-44" data-testid="conv-scope-staff"><SelectValue placeholder="By teammate…" /></SelectTrigger>
              <SelectContent>
                {staff.length === 0 ? <div className="px-2 py-1.5 text-sm text-gray-400">No teammates yet</div>
                  : staff.map((s) => <SelectItem key={s.id} value={s.id} data-testid={`conv-staff-opt-${s.id}`}>{s.name}{s.id === currentUser?.id ? ' (you)' : ''}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-6" data-testid="conversations-empty">No conversations to show.</p>
        ) : (
          <div className="space-y-2 max-h-[420px] overflow-y-auto">
            {rows.map((r) => (
              <QuickMessageButton key={r.user_id} userId={r.user_id} userName={r.user?.name} onClose={load}>
                <div className="flex items-center justify-between gap-3 border border-gray-100 rounded-lg p-3 cursor-pointer hover:bg-blue-50" data-testid={`conv-row-${r.user_id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    {r.user?.picture ? <img src={r.user.picture} alt="" className="w-9 h-9 rounded-full object-cover" />
                      : <div className="w-9 h-9 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-semibold">{(r.user?.name || '?').charAt(0).toUpperCase()}</div>}
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-800 truncate">{r.user?.name}</p>
                      <p className="text-xs text-gray-500 truncate">{r.last_from_staff ? 'Team: ' : ''}{r.last_body}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {r.unread > 0 && <Badge className="bg-rose-500 text-white" data-testid={`conv-unread-${r.user_id}`}>{r.unread} new</Badge>}
                    <span className="text-xs text-gray-400">{r.last_at ? new Date(r.last_at).toLocaleDateString() : ''}</span>
                  </div>
                </div>
              </QuickMessageButton>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};


const MessagesTab = ({ currentUser }) => {
  // --- Deep-link: open a specific member's thread from a notification (?user=<id>) ---
  const location = useLocation();
  const navigate = useNavigate();
  const [threadUser, setThreadUser] = useState(null);
  useEffect(() => {
    const uid = new URLSearchParams(location.search).get('user');
    if (!uid) { setThreadUser(null); return undefined; }
    let active = true;
    axios.get(`${API}/profile/${uid}`, authHeaders())
      .then((r) => { if (active) setThreadUser({ id: uid, name: r.data.name || 'Member' }); })
      .catch(() => { if (active) setThreadUser({ id: uid, name: 'Member' }); });
    return () => { active = false; };
  }, [location.search]);
  const closeThread = () => { setThreadUser(null); navigate('/admin?tab=messages', { replace: true }); };

  // --- Message a member (1:1) ---
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const searchUsers = useCallback(async (q) => {
    try { setResults((await axios.get(`${API}/admin/message-recipients?search=${encodeURIComponent(q || '')}`, authHeaders())).data); }
    catch (e) { toast.error('Search failed'); }
  }, []);
  useEffect(() => { searchUsers(''); }, [searchUsers]);
  useEffect(() => {
    const t = setTimeout(() => searchUsers(search), 350);
    return () => clearTimeout(t);
  }, [search, searchUsers]);

  // --- Broadcast ---
  const [mode, setMode] = useState('filter'); // filter | specific
  const [plan, setPlan] = useState('all');
  const [status, setStatus] = useState('active');
  const [segment, setSegment] = useState('none');
  const [bRepeat, setBRepeat] = useState('none');
  const [bRepeatUntil, setBRepeatUntil] = useState('');
  const [templates, setTemplates] = useState([]);
  const loadTemplates = useCallback(async () => {
    try { setTemplates((await axios.get(`${API}/admin/templates`, authHeaders())).data); } catch (e) {}
  }, []);
  useEffect(() => { loadTemplates(); }, [loadTemplates]);
  const [bBody, setBBody] = useState('');
  const [bTitle, setBTitle] = useState('');
  const [bStyle, setBStyle] = useState('normal');
  const [bSchedule, setBSchedule] = useState('');
  const [count, setCount] = useState(null);
  const [selected, setSelected] = useState([]);
  const [bSearch, setBSearch] = useState('');
  const [bResults, setBResults] = useState([]);
  const [sending, setSending] = useState(false);
  const [broadcasts, setBroadcasts] = useState([]);
  const loadBroadcasts = useCallback(async () => {
    try { setBroadcasts((await axios.get(`${API}/admin/broadcasts`, authHeaders())).data); } catch (e) {}
  }, []);
  useEffect(() => { loadBroadcasts(); }, [loadBroadcasts]);
  const cancelBroadcast = async (id) => {
    try { await axios.post(`${API}/admin/broadcasts/${id}/cancel`, {}, authHeaders()); toast.success('Scheduled broadcast cancelled'); loadBroadcasts(); }
    catch (e) { toast.error('Cancel failed'); }
  };

  const buildPayload = useCallback(() => (
    mode === 'specific'
      ? { user_ids: selected.map((s) => s.id) }
      : { filters: { plan, status, ...(segment !== 'none' ? { segment } : {}) } }
  ), [mode, selected, plan, status, segment]);

  const preview = useCallback(async () => {
    try { setCount((await axios.post(`${API}/admin/broadcast/preview`, buildPayload(), authHeaders())).data.count); }
    catch (e) { setCount(null); }
  }, [buildPayload]);
  useEffect(() => { preview(); }, [preview]);

  const bSearchUsers = async () => {
    try { setBResults((await axios.get(`${API}/admin/message-recipients?search=${encodeURIComponent(bSearch)}`, authHeaders())).data); }
    catch (e) {}
  };
  useEffect(() => {
    if (mode !== 'specific') return undefined;
    const t = setTimeout(bSearchUsers, 350);
    return () => clearTimeout(t);
  }, [bSearch, mode]);
  const toggleSel = (u) => setSelected((p) => (p.find((x) => x.id === u.id) ? p.filter((x) => x.id !== u.id) : [...p, u]));

  const sendBroadcast = async () => {
    setSending(true);
    try {
      const payload = { body: bBody.trim(), title: bTitle.trim() || null, style: bStyle, ...buildPayload() };
      if (bSchedule) payload.scheduled_at = new Date(bSchedule).toISOString();
      if (bRepeat !== 'none') { payload.repeat = bRepeat; if (bRepeatUntil) payload.repeat_until = new Date(bRepeatUntil).toISOString(); }
      const res = await axios.post(`${API}/admin/broadcast`, payload, authHeaders());
      if (res.data.scheduled) toast.success(`${res.data.repeat ? 'Recurring broadcast set' : 'Scheduled'} · ${res.data.count} member${res.data.count === 1 ? '' : 's'}`);
      else toast.success(`Message sent to ${res.data.sent} member${res.data.sent === 1 ? '' : 's'}`);
      setBBody(''); setBTitle(''); setBStyle('normal'); setBSchedule(''); setBRepeat('none'); setBRepeatUntil(''); setSelected([]); setBResults([]); setBSearch('');
      loadBroadcasts();
    } catch (e) { toast.error(e.response?.data?.detail || 'Broadcast failed'); }
    setSending(false);
  };

  const canSend = bBody.trim() && (mode === 'filter' ? (count || 0) > 0 : selected.length > 0);

  const insertTemplate = (id) => {
    const t = templates.find((x) => x.id === id);
    if (!t) return;
    setBTitle(t.title || ''); setBBody(t.body || ''); setBStyle(t.style || 'normal');
  };
  const saveTemplate = async () => {
    if (!bBody.trim()) return toast.error('Write a message first');
    const name = window.prompt('Template name:', bTitle || '');
    if (!name) return;
    try {
      await axios.post(`${API}/admin/templates`, { name, title: bTitle.trim() || null, body: bBody.trim(), style: bStyle }, authHeaders());
      toast.success('Template saved'); loadTemplates();
    } catch (e) { toast.error('Failed to save template'); }
  };

  return (
    <div className="space-y-8">
      <Dialog open={!!threadUser} onOpenChange={(v) => { if (!v) closeThread(); }}>
        {threadUser && <MessageThreadDialog userId={threadUser.id} userName={threadUser.name} onClose={() => {}} />}
      </Dialog>
      <RepliesInbox />
      <ConversationsList currentUser={currentUser} />
      {/* Message a member */}
      <Card className="shadow-xl border-blue-100" data-testid="message-member-card">
        <CardHeader><CardTitle className="flex items-center gap-2"><MessageCircle className="w-5 h-5 text-blue-500" /> Message a Member</CardTitle></CardHeader>
        <CardContent>
          <div className="flex gap-2 mb-3">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input data-testid="msg-user-search" placeholder="Search members by name or email..." value={search}
                onChange={(e) => setSearch(e.target.value)} className="pl-9"
                onKeyDown={(e) => { if (e.key === 'Enter') searchUsers(search); }} />
            </div>
            <Button variant="outline" onClick={() => searchUsers(search)}>Search</Button>
          </div>
          <div className="space-y-2 max-h-[320px] overflow-y-auto">
            {results.length === 0 ? <p className="text-gray-400 text-center py-6">No members found.</p> : results.map((u) => (
              <div key={u.id} className="flex items-center justify-between border border-gray-100 rounded-lg p-2" data-testid={`msg-recipient-${u.id}`}>
                <div className="flex items-center gap-2">
                  {u.picture ? <img src={u.picture} alt="" className="w-8 h-8 rounded-full object-cover" /> : <div className="w-8 h-8 rounded-full bg-blue-100" />}
                  <div><p className="text-sm font-medium">{u.name}</p><p className="text-xs text-gray-400">{u.email}</p></div>
                </div>
                <QuickMessageButton userId={u.id} userName={u.name} />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Broadcast */}
      <Card className="shadow-xl border-amber-100" data-testid="broadcast-card">
        <CardHeader><CardTitle className="flex items-center gap-2"><Megaphone className="w-5 h-5 text-amber-500" /> Broadcast a Message</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Button size="sm" variant={mode === 'filter' ? 'default' : 'outline'} data-testid="broadcast-mode-filter"
              className={mode === 'filter' ? 'bg-amber-500 hover:bg-amber-600' : ''} onClick={() => setMode('filter')}>By filter</Button>
            <Button size="sm" variant={mode === 'specific' ? 'default' : 'outline'} data-testid="broadcast-mode-specific"
              className={mode === 'specific' ? 'bg-amber-500 hover:bg-amber-600' : ''} onClick={() => setMode('specific')}>Selected members</Button>
          </div>

          {mode === 'filter' ? (
            <div className="flex gap-3 flex-wrap">
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase">Plan</label>
                <Select value={plan} onValueChange={setPlan}>
                  <SelectTrigger className="w-44 mt-1" data-testid="broadcast-plan"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All plans</SelectItem>
                    <SelectItem value="free">Free</SelectItem>
                    <SelectItem value="standard">Standard</SelectItem>
                    <SelectItem value="premium">Premium</SelectItem>
                    <SelectItem value="private_match">Private Match</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase">Account status</label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger className="w-40 mt-1" data-testid="broadcast-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="blocked">Blocked</SelectItem>
                    <SelectItem value="all">All</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase">Smart segment</label>
                <Select value={segment} onValueChange={setSegment}>
                  <SelectTrigger className="w-52 mt-1" data-testid="broadcast-segment"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="inactive_30">Inactive 30+ days</SelectItem>
                    <SelectItem value="inactive_90">Inactive 90+ days</SelectItem>
                    <SelectItem value="profile_incomplete">Profile incomplete</SelectItem>
                    <SelectItem value="free_members">Free members</SelectItem>
                    <SelectItem value="never_subscribed">Never subscribed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          ) : (
            <div>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input data-testid="broadcast-search" placeholder="Search members to add..." value={bSearch}
                    onChange={(e) => setBSearch(e.target.value)} className="pl-9"
                    onKeyDown={(e) => { if (e.key === 'Enter') bSearchUsers(); }} />
                </div>
                <Button variant="outline" onClick={bSearchUsers}>Search</Button>
              </div>
              {selected.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2" data-testid="broadcast-selected">
                  {selected.map((s) => (
                    <Badge key={s.id} className="bg-amber-100 text-amber-800 cursor-pointer" onClick={() => toggleSel(s)}>{s.name} <X className="w-3 h-3 ml-1" /></Badge>
                  ))}
                </div>
              )}
              <div className="space-y-1 max-h-[180px] overflow-y-auto mt-2">
                {bResults.map((u) => {
                  const isSel = selected.find((x) => x.id === u.id);
                  return (
                    <div key={u.id} className="flex items-center justify-between border border-gray-100 rounded-lg p-2" data-testid={`broadcast-cand-${u.id}`}>
                      <div><p className="text-sm font-medium">{u.name}</p><p className="text-xs text-gray-400">{u.email}</p></div>
                      <Button size="sm" variant={isSel ? 'outline' : 'ghost'} className="text-amber-600" data-testid={`broadcast-add-${u.id}`} onClick={() => toggleSel(u)}>{isSel ? 'Remove' : 'Add'}</Button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex gap-3 flex-wrap items-end">
            <div className="flex-1 min-w-[220px]">
              <label className="text-xs font-semibold text-gray-500 uppercase">Title (optional)</label>
              <Input data-testid="broadcast-title" value={bTitle} onChange={(e) => setBTitle(e.target.value)} className="mt-1" placeholder="e.g. Platform Update" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase">Type</label>
              <div className="flex gap-2 mt-1">
                <Button size="sm" variant={bStyle === 'normal' ? 'default' : 'outline'} data-testid="broadcast-style-normal"
                  className={bStyle === 'normal' ? 'bg-rose-500 hover:bg-rose-600' : ''} onClick={() => setBStyle('normal')}>Casual</Button>
                <Button size="sm" variant={bStyle === 'important' ? 'default' : 'outline'} data-testid="broadcast-style-important"
                  className={bStyle === 'important' ? 'bg-amber-500 hover:bg-amber-600 text-white' : ''} onClick={() => setBStyle('important')}>Important</Button>
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase">Send later (optional)</label>
              <Input type="datetime-local" data-testid="broadcast-schedule" value={bSchedule} onChange={(e) => setBSchedule(e.target.value)} className="mt-1 w-56" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase">Repeat</label>
              <Select value={bRepeat} onValueChange={setBRepeat}>
                <SelectTrigger className="w-36 mt-1" data-testid="broadcast-repeat"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">One-time</SelectItem>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {bRepeat !== 'none' && (
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase">Repeat until (optional)</label>
                <Input type="datetime-local" data-testid="broadcast-repeat-until" value={bRepeatUntil} onChange={(e) => setBRepeatUntil(e.target.value)} className="mt-1 w-56" />
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <Select value="" onValueChange={insertTemplate}>
              <SelectTrigger className="w-56" data-testid="broadcast-template-select"><SelectValue placeholder="Insert a template…" /></SelectTrigger>
              <SelectContent>
                {templates.length === 0 ? <div className="px-2 py-1.5 text-sm text-gray-400">No templates yet</div>
                  : templates.map((t) => <SelectItem key={t.id} value={t.id} data-testid={`template-opt-${t.id}`}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" data-testid="save-template-btn" onClick={saveTemplate}>Save as template</Button>
          </div>

          <Textarea data-testid="broadcast-body" value={bBody} onChange={(e) => setBBody(e.target.value)} rows={4}
            className="resize-none" placeholder="Write your message to members. They'll receive it in their Concierge inbox and can reply." />

          <div className="flex items-center justify-between flex-wrap gap-3">
            <p className="text-sm text-gray-500" data-testid="broadcast-count">
              {mode === 'specific'
                ? `${selected.length} member${selected.length === 1 ? '' : 's'} selected`
                : (count === null ? 'Calculating recipients…' : `${count} member${count === 1 ? '' : 's'} will receive this`)}
            </p>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button data-testid="broadcast-send-btn" disabled={!canSend || sending} className="bg-amber-500 hover:bg-amber-600 text-white">
                  <Megaphone className="w-4 h-4 mr-2" /> {sending ? 'Sending…' : (bSchedule ? 'Schedule Broadcast' : 'Send Broadcast')}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="broadcast-confirm-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle>{bSchedule ? 'Schedule this message?' : 'Send this message?'}</AlertDialogTitle>
                  <AlertDialogDescription>
                    {bSchedule
                      ? `This will be sent on ${bSchedule ? new Date(bSchedule).toLocaleString() : ''} to ${mode === 'specific' ? selected.length : (count ?? 0)} member${(mode === 'specific' ? selected.length : (count ?? 0)) === 1 ? '' : 's'}.`
                      : `This will send your message to ${mode === 'specific' ? selected.length : (count ?? 0)} member${(mode === 'specific' ? selected.length : (count ?? 0)) === 1 ? '' : 's'}. It lands in each member's Concierge inbox and they can reply.`}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="broadcast-cancel">Cancel</AlertDialogCancel>
                  <AlertDialogAction data-testid="broadcast-confirm" onClick={sendBroadcast} className="bg-amber-500 hover:bg-amber-600">{bSchedule ? 'Schedule' : 'Send now'}</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </CardContent>
      </Card>

      {/* Broadcast History */}
      <Card className="shadow-xl border-gray-100" data-testid="broadcast-history-card">
        <CardHeader><CardTitle className="flex items-center gap-2"><ScrollText className="w-5 h-5 text-gray-500" /> Broadcast History</CardTitle></CardHeader>
        <CardContent>
          {broadcasts.length === 0 ? <p className="text-gray-400 text-center py-6">No broadcasts yet.</p> : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Message</TableHead><TableHead>Sent by</TableHead><TableHead>Audience</TableHead>
                  <TableHead>Recipients</TableHead><TableHead>Read</TableHead><TableHead>Status</TableHead><TableHead>When</TableHead><TableHead></TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {broadcasts.map((b) => (
                    <TableRow key={b.id} data-testid={`broadcast-row-${b.id}`}>
                      <TableCell className="max-w-[240px]">
                        {b.title && <span className="font-semibold block">{b.title}</span>}
                        <span className="text-gray-500 text-sm line-clamp-1">{b.body}</span>
                        {b.style === 'important' && <Badge className="bg-amber-100 text-amber-700 mt-1">Important</Badge>}
                        {b.repeat && <Badge className="bg-purple-100 text-purple-700 mt-1 ml-1">Repeats {b.repeat}</Badge>}
                      </TableCell>
                      <TableCell className="text-sm">{b.sender_name}</TableCell>
                      <TableCell className="text-sm">{b.audience}</TableCell>
                      <TableCell>{b.recipient_count}</TableCell>
                      <TableCell className="text-sm" data-testid={`broadcast-readrate-${b.id}`}>
                        {b.status === 'sent' && b.recipient_count
                          ? `${b.read_count || 0}/${b.recipient_count} (${Math.round(((b.read_count || 0) / b.recipient_count) * 100)}%)`
                          : '—'}
                      </TableCell>
                      <TableCell>
                        <Badge className={b.status === 'sent' ? 'bg-green-100 text-green-700' : b.status === 'scheduled' ? 'bg-blue-100 text-blue-700' : b.status === 'cancelled' ? 'bg-gray-200 text-gray-600' : 'bg-amber-100 text-amber-700'}>
                          {b.status.charAt(0).toUpperCase() + b.status.slice(1)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-gray-500">
                        {b.status === 'scheduled' ? (b.scheduled_at ? new Date(b.scheduled_at).toLocaleString() : '-') : (b.sent_at ? new Date(b.sent_at).toLocaleString() : (b.created_at ? new Date(b.created_at).toLocaleString() : '-'))}
                      </TableCell>
                      <TableCell className="text-right">
                        {b.status === 'scheduled' && (
                          <Button size="sm" variant="ghost" className="text-red-600" data-testid={`cancel-broadcast-${b.id}`} onClick={() => cancelBroadcast(b.id)}>Cancel</Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const ResultUploadDialog = ({ requestId, memberName, existing, onUploaded }) => {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!file) { toast.error('Choose a file'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (note) fd.append('note', note);
      await axios.post(`${API}/admin/services/${requestId}/result`, fd, {
        headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}`, 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Result delivered to member');
      setOpen(false); setFile(null); setNote('');
      onUploaded && onUploaded();
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="ghost" className={existing ? 'text-green-600' : 'text-amber-600'} data-testid={`upload-result-${requestId}`} title="Background check result">
          <ScrollText className="w-4 h-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md" data-testid="result-upload-dialog">
        <DialogHeader><DialogTitle>Background Check Result · {memberName}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {existing && (
            <div className="text-xs bg-green-50 text-green-700 rounded-lg p-2" data-testid="existing-result">
              Current file: <span className="font-medium">{existing.original_filename}</span> (uploading a new one replaces it)
            </div>
          )}
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase">Result file (PDF, image, doc — max 15MB)</label>
            <Input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.csv,.txt" data-testid="result-file-input"
              onChange={(e) => setFile(e.target.files?.[0] || null)} className="mt-1" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase">Note to member (optional)</label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="resize-none mt-1" data-testid="result-note-input" placeholder="e.g. Verification complete — all clear." />
          </div>
          <Button onClick={submit} disabled={busy} className="bg-rose-600 hover:bg-rose-700 w-full" data-testid="result-submit-btn">
            {busy ? 'Uploading…' : 'Deliver Result'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ConciergeTab = ({ role }) => {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [staff, setStaff] = useState([]);
  const isManager = ['super_admin', 'moderator', 'support_lead'].includes(role);
  const load = useCallback(async () => {
    try {
      const q = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      setItems((await axios.get(`${API}/admin/services${q}`, authHeaders())).data);
    } catch (e) { toast.error('Failed to load services'); }
  }, [statusFilter]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    axios.get(`${API}/admin/staff`, authHeaders()).then((r) => setStaff(r.data)).catch(() => setStaff([]));
  }, []);

  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
  };

  return (
    <div className="space-y-8">
    <Card className="shadow-xl border-rose-100">
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <CardTitle className="flex items-center gap-2"><ConciergeBell className="w-5 h-5" /> Service Requests</CardTitle>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-44" data-testid="service-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {SVC_STATUS.map((s) => <SelectItem key={s} value={s}>{SVC_STATUS_LABEL[s]}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? <p className="text-gray-500 py-8 text-center">No service requests.</p> : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Member</TableHead><TableHead>Service</TableHead><TableHead>Status</TableHead>
                <TableHead>Assigned</TableHead><TableHead>Requested</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {items.map((it) => (
                  <TableRow key={it.id} data-testid={`service-row-${it.id}`}>
                    <TableCell className="font-medium">{it.user?.name}<span className="block text-xs text-gray-400">{it.user?.email}</span></TableCell>
                    <TableCell><Badge variant="outline">{SERVICE_LABEL[it.service_type] || it.service_type}</Badge></TableCell>
                    <TableCell>
                      <Select value={it.status} onValueChange={(v) => act(() => axios.patch(`${API}/admin/services/${it.id}`, { status: v }, authHeaders()), `Status: ${SVC_STATUS_LABEL[v]}`)}>
                        <SelectTrigger className="h-8 w-36" data-testid={`service-status-${it.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{SVC_STATUS.map((s) => <SelectItem key={s} value={s}>{SVC_STATUS_LABEL[s]}</SelectItem>)}</SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-sm text-gray-600">
                      {isManager ? (
                        <Select value={it.assigned_to || 'unassign'} onValueChange={(v) => act(() => axios.post(`${API}/admin/services/${it.id}/assign${v === 'unassign' ? '?staff_id=unassign' : `?staff_id=${v}`}`, {}, authHeaders()), v === 'unassign' ? 'Unassigned' : 'Assigned')}>
                          <SelectTrigger className="h-8 w-40" data-testid={`assign-select-${it.id}`}><SelectValue placeholder="Unassigned" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="unassign">Unassigned</SelectItem>
                            {staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} · {s.role_label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      ) : (it.assigned_name || <span className="text-gray-400">Unassigned</span>)}
                    </TableCell>
                    <TableCell className="text-sm text-gray-500">{it.created_at ? new Date(it.created_at).toLocaleDateString() : '-'}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <MemberManageDialog userId={it.user_id} userName={it.user?.name} onChanged={load}>
                        <Button size="sm" variant="ghost" className="text-indigo-600" data-testid={`manage-${it.id}`}>
                          <Heart className="w-4 h-4" />
                        </Button>
                      </MemberManageDialog>
                      <Button size="sm" variant="ghost" className="text-indigo-600" data-testid={`assign-${it.id}`}
                        onClick={() => act(() => axios.post(`${API}/admin/services/${it.id}/assign`, {}, authHeaders()), 'Assigned to you')}>
                        <UserPlus className="w-4 h-4" />
                      </Button>
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button size="sm" variant="ghost" className="text-rose-600 relative" data-testid={`message-${it.id}`}>
                            <MessageCircle className="w-4 h-4" />
                            {it.unread > 0 && <span className="absolute -top-1 -right-1 bg-rose-600 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center">{it.unread}</span>}
                          </Button>
                        </DialogTrigger>
                        <MessageThreadDialog userId={it.user_id} userName={it.user?.name} />
                      </Dialog>
                      {it.service_type === 'background_check' && (
                        <ResultUploadDialog requestId={it.id} memberName={it.user?.name} existing={it.result_file} onUploaded={load} />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
    <ProposalsCard />
    </div>
  );
};

const PROP_STATUS_BADGE = {
  proposed: 'bg-amber-100 text-amber-700', mutual: 'bg-green-100 text-green-700',
  declined: 'bg-gray-200 text-gray-600', withdrawn: 'bg-gray-200 text-gray-600',
};

const ProposalsCard = () => {
  const [items, setItems] = useState([]);
  const [mstats, setMstats] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/proposals`, authHeaders()).then((r) => setItems(r.data)).catch(() => {});
    axios.get(`${API}/admin/matchmaker/stats`, authHeaders()).then((r) => setMstats(r.data)).catch(() => {});
  }, []);
  const respLabel = (r) => r === 'accept' ? '✅' : r === 'decline' ? '❌' : '⏳';
  return (
    <Card className="shadow-xl border-rose-100">
      <CardHeader><CardTitle className="flex items-center gap-2"><Heart className="w-5 h-5" /> Curated Introductions ({items.length})</CardTitle></CardHeader>
      <CardContent>
        {mstats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5" data-testid="matchmaker-stats">
            <div className="bg-green-50 rounded-lg p-3 text-center"><p className="text-2xl font-bold text-green-700">{mstats.mutual_matches}</p><p className="text-xs text-gray-500">Matches Made</p></div>
            <div className="bg-amber-50 rounded-lg p-3 text-center"><p className="text-2xl font-bold text-amber-700">{mstats.pending}</p><p className="text-xs text-gray-500">Awaiting Response</p></div>
            <div className="bg-rose-50 rounded-lg p-3 text-center"><p className="text-2xl font-bold text-rose-700">{mstats.total_proposals}</p><p className="text-xs text-gray-500">Total Proposed</p></div>
            <div className="bg-indigo-50 rounded-lg p-3 text-center"><p className="text-2xl font-bold text-indigo-700">{mstats.success_rate}%</p><p className="text-xs text-gray-500">Success Rate</p></div>
          </div>
        )}
        {items.length === 0 ? <p className="text-gray-500 py-6 text-center">No introductions proposed yet. Use the ♥ Manage button to propose a match.</p> : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Member</TableHead><TableHead>Candidate</TableHead><TableHead>Responses</TableHead><TableHead>Status</TableHead><TableHead>Proposed by</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {items.map((p) => (
                  <TableRow key={p.id} data-testid={`proposal-row-${p.id}`}>
                    <TableCell className="font-medium">{p.member?.name}</TableCell>
                    <TableCell className="font-medium">{p.candidate?.name}</TableCell>
                    <TableCell className="text-sm">M {respLabel(p.member_response)} · C {respLabel(p.candidate_response)}</TableCell>
                    <TableCell><Badge className={PROP_STATUS_BADGE[p.status] || ''}>{p.status === 'mutual' ? 'Matched 🎉' : p.status}</Badge></TableCell>
                    <TableCell className="text-sm text-gray-500">{p.proposed_by_name}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminPage;
